import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  debounce,
  Box,
  Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { ValidDataService } from '../../service/validDataService';
import FrameSelectDialog from '../admin/inventory/frames/frameSelectDialog';

export default function FrameInventorySelector({
  value = null,
  onChange,
  locationId = null,
  locationName = '',
  label = 'Frame',
  disabled = false,
  helperText = '',
  error = false
}) {
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    if (value?.modelNumber) {
      setInputValue(value.modelNumber);
    }
  }, [value?.id, value?.modelNumber]);

  const fetchFrames = useMemo(() => debounce(async (request, callback) => {
    const result = await ValidDataService.searchFrameInventory(request);
    callback(result);
  }, 400), []);

  useEffect(() => {
    let active = true;
    const trimmedInput = inputValue.trim();

    if (!trimmedInput || trimmedInput.length < 2) {
      setLoading(false);
      setOptions(value ? [value] : []);
      return undefined;
    }

    setLoading(true);
    fetchFrames({ q: trimmedInput, locationId, limit: 20 }, (result) => {
      if (!active) {
        return;
      }

      setLoading(false);
      const { code, data } = result || {};
      if (code !== 0) {
        setOptions(value ? [value] : []);
        return;
      }

      const nextOptions = value ? [value, ...(data || []).filter(option => option.id !== value.id)] : (data || []);
      setOptions(nextOptions);
    });

    return () => {
      active = false;
    };
  }, [fetchFrames, inputValue, locationId, value]);

  const handleDialogSelect = (item) => {
    onChange?.(item);
    setDialogOpen(false);
  };

  return (
    <>
      <Autocomplete
        filterOptions={(x) => x}
        fullWidth
        size="small"
        options={options}
        value={value}
        disabled={disabled}
        loading={loading}
        autoComplete
        includeInputInList
        filterSelectedOptions
        isOptionEqualToValue={(option, selected) => option.id === selected.id}
        getOptionLabel={(option) => typeof option === 'string' ? option : option?.modelNumber || ''}
        noOptionsText={inputValue.trim().length < 2 ? 'Type at least 2 characters' : 'No frames found'}
        onChange={(_, newValue) => onChange?.(newValue || null)}
        onInputChange={(_, newInputValue, reason) => {
          if (reason === 'clear') {
            setInputValue('');
            onChange?.(null);
            return;
          }
          setInputValue(newInputValue);
        }}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="body2" fontWeight={600}>
                {option.modelNumber || `Frame #${option.id}`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {[option.properties?.color, option.properties?.sizeCode || option.properties?.size, option.brand?.name, option.location?.name]
                  .filter(Boolean)
                  .join(' | ')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {[option.internalUpc || option.sku, option.supplier?.supplierName]
                  .filter(Boolean)
                  .join(' | ')}
              </Typography>
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            error={error}
            helperText={helperText || (locationName ? `Scoped to ${locationName}` : '')}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={18} /> : null}
                  <InputAdornment position="end">
                    <Tooltip title="Browse frames">
                      <span>
                        <IconButton
                          size="small"
                          edge="end"
                          onClick={() => setDialogOpen(true)}
                          disabled={disabled}
                        >
                          <SearchIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </InputAdornment>
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
        )}
      />

      <FrameSelectDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSelect={handleDialogSelect}
        locationId={locationId}
        locationName={locationName}
      />
    </>
  );
}